import { personPatch } from '@hr/api'
import { AgGridReact } from 'ag-grid-react'
import { useCallback, useRef, useState } from 'react'
import { Accordion, Row } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { getLocaleText } from '../../../../../actions/datagrid'
import { contextMenu, defaultColDef, gridSettings, sideBar, statusBar } from '../../../../../common/grid'
import { BooleanFormatter, BooleanRenderer } from '../../../../../common/gridcomponents'
import { PdfReport } from '../../../../../common/pdfreport'
import { dateFormatter, dateGetter, localized, numberFormatter } from '../../../../../common/utils'
import { authSelector } from '../../../../../reducers'

export function SalaryHistory({ salaryHistory }) {
	const { t } = useTranslation()
	const { supplements } = useSelector(authSelector)

	const [show, setShow] = useState(false)

	const translatedStatus = {
		ACCEPTED: t('Accepted', 'Hyväksytty'),
		CANCELED: t('Canceled', 'Peruutettu'),
		PENDING: t('Pending', 'Odottaa'),
		OPEN: t('Open', 'Avoinna'),
	}

	const columnDefs = [
		{
			field: 'nr',
			headerName: t('PersonNumber', 'Henkilönumero'),
		},
		{
			field: 'salaryValidFrom',
			headerName: t('ValidFrom', 'Voimassaolon alkupvm.'),
			cellClass: 'dateType',
			filter: 'agDateColumnFilter',
			valueFormatter: dateFormatter,
		},
		{
			field: 'salaryValidUntil',
			headerName: t('ValidUntil', 'Voimassaolon päättymispvm.'),
			cellClass: 'dateType',
			filter: 'agDateColumnFilter',
			valueFormatter: dateFormatter,
		},
		{
			field: 'employmentType',
			headerName: t('EmploymentType', 'Palvelussuhteen tyyppi'),
			filter: 'agSetColumnFilter',
			valueGetter: ({ data, colDef }) => localized.employmentTypes[data[colDef.field]],
		},
		{
			field: 'partTimePercentage',
			headerName: t('PartTimePercentage', 'Osa-aikaprosentti'),
			valueFormatter: numberFormatter,
		},
		{
			field: 'monthlyWage',
			headerName: t('MonthlyWage', 'Kuukausipalkka'),
			valueFormatter: numberFormatter,
		},
		{
			field: 'hourlyWageATP',
			headerName: t('HourlyWageATP', 'Aikatuntipalkka'),
			valueFormatter: numberFormatter,
		},
		{
			field: 'hourlyWageOTP',
			headerName: t('HourlyWageOTP', 'Ohjetuntipalkka'),
			valueFormatter: numberFormatter,
		},
		{
			field: 'hourlyWagePTP',
			headerName: t('HourlyWagePTP', 'Perustuntipalkka'),
			valueFormatter: numberFormatter,
		},
		{
			field: 'paymentPercentage',
			headerName: t('PaymentPercentage', 'Maksukerroin (%)'),
			valueFormatter: numberFormatter,
		},
		{
			field: 'phoneBenefit',
			headerName: t('PhoneBenefit', 'Puhelinetu'),
			cellRenderer: BooleanRenderer,
			filter: 'agSetColumnFilter',
			filterParams: { valueFormatter: BooleanFormatter },
		},
		{
			field: 'bicycleBenefit',
			headerName: t('BicycleBenefit', 'Polkupyöräetu'),
			cellRenderer: BooleanRenderer,
			filter: 'agSetColumnFilter',
			filterParams: { valueFormatter: BooleanFormatter },
		},
		{
			field: 'mealBenefit',
			headerName: t('MealBenefit', 'Ravintoetu'),
			cellRenderer: BooleanRenderer,
			filter: 'agSetColumnFilter',
			filterParams: { valueFormatter: BooleanFormatter },
		},
		{
			field: 'carUsageBenefit',
			headerName: t('CarUsageBenefit', 'Auton käyttöetu'),
			cellRenderer: BooleanRenderer,
			filter: 'agSetColumnFilter',
			filterParams: { valueFormatter: BooleanFormatter },
		},
		{
			field: 'freeCarBenefit',
			headerName: t('FullCarBenefit', 'Auton vapaa käyttöetu'),
			cellRenderer: BooleanRenderer,
			filter: 'agSetColumnFilter',
			filterParams: { valueFormatter: BooleanFormatter },
		},
		...Array.from({ length: 16 }, (_, i) => ({
			field: `supplement${i + 1}`,
			headerName: supplements[i] || `${t('Supplement', 'Lisä')} ${i + 1}`,
			valueFormatter: numberFormatter,
		})),
		...Array.from({ length: 4 }, (_, i) => ({
			field: `fixedAnnualSupplement${i + 1}`,
			headerName: `${t('FixedAnnualSupplement', 'Määrävuosilisä')} ${i + 1}`,
			valueFormatter: numberFormatter,
		})),
		...Array.from({ length: 4 }, (_, i) => ({
			field: `annualSupplement${i + 1}`,
			headerName: `${t('AnnualSupplement', 'Vuosisidonnainen lisä')} ${i + 1}`,
			valueFormatter: numberFormatter,
		})),
		{
			field: 'createdBy',
			headerName: t('CreatedBy', 'Luonut'),
		},
		{
			field: 'createdAt',
			headerName: t('CreatedAt', 'Luotu'),
			filter: 'agDateColumnFilter',
			valueGetter: dateGetter,
			valueFormatter: dateFormatter,
		},
		{
			field: 'status',
			headerName: t('Status', 'Tila'),
			lockVisible: true,
			suppressColumnsToolPanel: true,
			editable: true,
			cellEditor: 'agRichSelectCellEditor',
			cellEditorParams: {
				values: ['ACCEPTED', 'CANCELED', 'PENDING', 'OPEN'],
				formatValue: (value) => translatedStatus[value] || value,
			},
			cellRenderer: (params) => {
				return (
					<span style={{ display: 'flex', justifyContent: 'space-between', cursor: 'pointer' }}>
						<span>{translatedStatus[params.value] || params.value}</span>
						<span style={{ opacity: 0.6 }}>▼</span>
					</span>
				)
			},
			valueSetter: async ({ newValue, colDef, data }) => {
				const person = {
					personId: data.personId,
					nr: data.nr,
					salaryDetails: { status: newValue },
				}
				data[colDef.field] = newValue
				await personPatch(person.nr, person) // Patch person with new status
				return true // value was successfully updated
			},
			filter: 'agSetColumnFilter',
		},
		{
			field: 'statusChangedBy',
			headerName: t('StatusChangedBy', 'Tilaa muutti'),
		},
		{
			field: 'statusChangedAt',
			headerName: t('StatusChangedAt', 'Tila muutettu'),
			filter: 'agDateColumnFilter',
			valueGetter: dateGetter,
			valueFormatter: dateFormatter,
		},
	]

	const { companyName } = useSelector(authSelector)
	const gridRef = useRef()
	const [report, setReport] = useState(null)
	const closeReport = () => setReport(null)
	const getContextMenuItems = useCallback(
		(params) => {
			return contextMenu(t, params, setReport, {
				title: t('SalaryHistory', 'Palkkahistoria'),
				companyname: companyName,
			})
		},
		[t, companyName],
	)

	return (
		<Accordion.Item key="salaryhistory" eventKey="salaryhistory">
			<Accordion.Header>{t('SalaryHistory', 'Palkkahistoria')}</Accordion.Header>
			<Accordion.Body className="d-flex flex-column h-450" onEnter={() => setShow(true)}>
				{show && (
					<>
						{report !== null && <PdfReport json={report} onCloseReport={closeReport} />}
						<Row className="ag-theme-material styles-ag flex-grow-1 mt-3">
							<AgGridReact
								{...gridSettings}
								ref={gridRef}
								columnDefs={columnDefs}
								defaultColDef={{ ...defaultColDef, filter: 'agNumberColumnFilter' }}
								sideBar={sideBar}
								rowData={salaryHistory}
								getContextMenuItems={getContextMenuItems}
								statusBar={statusBar}
								getLocaleText={(params) => getLocaleText(t, params)}
								animateRows
								suppressCellFocus
								enableCellTextSelection
								singleClickEdit
								debug={import.meta.env.DEV}
							/>
						</Row>
					</>
				)}
			</Accordion.Body>
		</Accordion.Item>
	)
}
